import { c as _c } from "react/compiler-runtime";
import { Button, Needle, Trash } from '@sprd/sprd-component-kit/v2';
import { useIsMutating } from '@tanstack/react-query';
import { useMemo } from 'react';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { LineItemGroup } from '@/types/commercetools';
import { useCart, useRemoveItemGroup } from '@/hooks/useCartHooks';
import { useCentPriceFormatter } from '@/hooks/usePriceFormatters';
import useProductType from '@/hooks/useProductType';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { getBasketItemPrices } from '@/utility/getBasketItemPrices';
import { getAppearanceName, getEditLink, getSizeId, isEmbroideryConfiguration } from '@/utility/lineItems/lineItemHelpers';
import { formatCharactersForUrlPathname } from '@/utility/urlPathnameSanitizer';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';
import { BasketSize } from './BasketSize';
const getCTTrackedPrice = (price: number, discount: number) => {
  const hasDiscount = discount > 0;
  return hasDiscount ? +((price - discount) / 100).toFixed(2) : +(price / 100).toFixed(2);
};
const StrikeThroughPrice = t0 => {
  const $ = _c(16);
  const {
    price,
    discount,
    formatPrice
  } = t0;
  const hasDiscount = discount > 0;
  const t1 = hasDiscount ? "text-neutral-700 line-through" : "";
  let t2;
  if ($[0] !== t1) {
    t2 = clsx(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== formatPrice || $[3] !== price) {
    t3 = formatPrice(price);
    $[2] = formatPrice;
    $[3] = price;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== discount || $[9] !== formatPrice || $[10] !== hasDiscount || $[11] !== price) {
    t5 = hasDiscount && <span className="ml-2 text-red-600">{formatPrice(price - discount)}</span>;
    $[8] = discount;
    $[9] = formatPrice;
    $[10] = hasDiscount;
    $[11] = price;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const BasketItem = t0 => {
  const $ = _c(97);
  const {
    lineItemGroup
  } = t0;
  const t = useTranslations("miniBasket");
  const {
    data: cartAndLineItemGroups,
    isLoading: isCartLoading
  } = useCart();
  const removeItemGroup = useRemoveItemGroup();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mutationKey: ["cart-action"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isMutating = useIsMutating(t1) > 0;
  const locale = useLocale();
  const imageBaseUrl = useImageBaseUrl();
  const formatPrice = useCentPriceFormatter(lineItemGroup.currencyCode);
  const {
    data: productType,
    isLoading: isProductTypeLoading
  } = useProductType(lineItemGroup.shopId, lineItemGroup.productTypeId, locale);
  const {
    lineItems,
    productLookupId,
    sellableId,
    productTypeId,
    appearanceId
  } = lineItemGroup;
  const name = productType?.name ?? lineItemGroup.name;
  const compositionId = lineItems[0]?.custom.fields.sprdCompositionId;
  let t2;
  if ($[1] !== lineItemGroup.configurationDetails) {
    t2 = isEmbroideryConfiguration(lineItemGroup.configurationDetails);
    $[1] = lineItemGroup.configurationDetails;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isEmbroidery = t2;
  let t3;
  if ($[3] !== name) {
    t3 = formatCharactersForUrlPathname(name, "-", false);
    $[3] = name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const imageSrc = `${imageBaseUrl}/products/${productLookupId}/${t3}.webp`;
  let t4;
  bb0: {
    let t5;
    if ($[5] !== productType?.sizes) {
      t5 = productType?.sizes ?? [];
      $[5] = productType?.sizes;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    const sizes = t5;
    if (!sizes.length) {
      t4 = lineItems;
      break bb0;
    }
    let t6;
    if ($[7] !== lineItems || $[8] !== sizes) {
      let t7;
      if ($[10] !== sizes) {
        t7 = (a, b) => {
          const indexA = sizes.findIndex(s => +s.id === getSizeId(a));
          const indexB = sizes.findIndex(s_0 => +s_0.id === getSizeId(b));
          return indexA - indexB;
        };
        $[10] = sizes;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      t6 = lineItems.toSorted(t7);
      $[7] = lineItems;
      $[8] = sizes;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t4 = t6;
  }
  const sortedLineItems = t4;
  const cartId = cartAndLineItemGroups?.cart.id;
  let t5;
  if ($[12] !== lineItemGroup) {
    t5 = getBasketItemPrices(lineItemGroup);
    $[12] = lineItemGroup;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    groupPrice,
    singlePrice,
    groupDiscount,
    singleDiscount
  } = t5;
  const isLoading = isCartLoading || isProductTypeLoading;
  const t6 = lineItemGroup.groupId;
  let t7;
  if ($[14] !== cartId || $[15] !== imageSrc || $[16] !== lineItemGroup || $[17] !== name) {
    t7 = getEditLink(lineItemGroup, cartId) ? <Link href={getEditLink(lineItemGroup, cartId)}><img width={160} height={213} src={imageSrc} alt={name} className="aspect-3/4 max-w-40 bg-neutral-100 object-contain" /></Link> : <img width={160} height={213} src={imageSrc} alt={name} className="aspect-3/4 max-w-40 bg-neutral-100 object-contain" />;
    $[14] = cartId;
    $[15] = imageSrc;
    $[16] = lineItemGroup;
    $[17] = name;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== formatPrice || $[20] !== singleDiscount || $[21] !== singlePrice) {
    t8 = <StrikeThroughPrice price={singlePrice} discount={singleDiscount} formatPrice={formatPrice} />;
    $[19] = formatPrice;
    $[20] = singleDiscount;
    $[21] = singlePrice;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== appearanceId || $[24] !== compositionId || $[25] !== lineItemGroup.shopId || $[26] !== name || $[27] !== productTypeId || $[28] !== sellableId || $[29] !== singleDiscount || $[30] !== singlePrice) {
    t9 = sellableId && compositionId && <FavoriteButton sellableId={sellableId} compositionId={compositionId} productTypeId={String(productTypeId)} appearanceId={String(appearanceId)} label={name} iconClassName="size-5!" shopId={lineItemGroup.shopId} price={getCTTrackedPrice(singlePrice, singleDiscount)} />;
    $[23] = appearanceId;
    $[24] = compositionId;
    $[25] = lineItemGroup.shopId;
    $[26] = name;
    $[27] = productTypeId;
    $[28] = sellableId;
    $[29] = singleDiscount;
    $[30] = singlePrice;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t) {
    t10 = t("deleteProduct");
    $[32] = t;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== lineItemGroup || $[35] !== removeItemGroup) {
    t11 = () => removeItemGroup.mutate({
      lineItemGroup
    });
    $[34] = lineItemGroup;
    $[35] = removeItemGroup;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Trash className="size-5 cursor-pointer" />;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== isMutating || $[39] !== t10 || $[40] !== t11) {
    t13 = <Button variant="plain" className="p-0!" disabled={isMutating} aria-label={t10} onClick={t11}>{t12}</Button>;
    $[38] = isMutating;
    $[39] = t10;
    $[40] = t11;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t9) {
    t14 = <div className="flex items-center gap-1">{t9}{t13}</div>;
    $[42] = t13;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t14 || $[46] !== t8) {
    t15 = <div className="mb-1.5 flex items-center justify-between">{t8}{t14}</div>;
    $[45] = t14;
    $[46] = t8;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== cartId || $[49] !== lineItemGroup || $[50] !== name) {
    t16 = getEditLink(lineItemGroup, cartId) ? <Link href={getEditLink(lineItemGroup, cartId)} className="mb-1 hover:text-neutral-700">{name}</Link> : null;
    $[48] = cartId;
    $[49] = lineItemGroup;
    $[50] = name;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== productType?.appearances) {
    t17 = productType?.appearances ?? [];
    $[52] = productType?.appearances;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== lineItemGroup.appearanceId || $[55] !== t17) {
    t18 = getAppearanceName(t17, lineItemGroup.appearanceId);
    $[54] = lineItemGroup.appearanceId;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t18) {
    t19 = <span className="text-neutral-700">{t18}</span>;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t15 || $[60] !== t16 || $[61] !== t19) {
    t20 = <div className="flex flex-col">{t15}{t16}{t19}</div>;
    $[59] = t15;
    $[60] = t16;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== isEmbroidery || $[64] !== t) {
    t21 = isEmbroidery && <div className="inline-flex w-fit items-center gap-1 rounded-sm border border-neutral-200 bg-white px-1 py-0.5 text-black sm:px-3 sm:py-1"><Needle className="size-4" />{t("embroidery")}</div>;
    $[63] = isEmbroidery;
    $[64] = t;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== lineItemGroup || $[67] !== sortedLineItems) {
    let t23;
    if ($[69] !== lineItemGroup) {
      t23 = lineItem => <BasketSize lineItem={lineItem} lineItemGroup={lineItemGroup} key={`${lineItem.id}-${lineItem.quantity}`} />;
      $[69] = lineItemGroup;
      $[70] = t23;
    } else {
      t23 = $[70];
    }
    t22 = sortedLineItems.map(t23);
    $[66] = lineItemGroup;
    $[67] = sortedLineItems;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[71] !== t22) {
    t23 = <div className="flex flex-col gap-2">{t22}</div>;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== t20 || $[74] !== t21 || $[75] !== t23) {
    t24 = <div className="flex w-full flex-col gap-3">{t20}{t21}{t23}</div>;
    $[73] = t20;
    $[74] = t21;
    $[75] = t23;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== t24 || $[78] !== t7) {
    t25 = <div className="flex gap-4">{t7}{t24}</div>;
    $[77] = t24;
    $[78] = t7;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== lineItemGroup.groupQuantity || $[81] !== t) {
    t26 = t("items", {
      count: lineItemGroup.groupQuantity
    });
    $[80] = lineItemGroup.groupQuantity;
    $[81] = t;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== t26) {
    t27 = <span>{t26}</span>;
    $[83] = t26;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== formatPrice || $[86] !== groupDiscount || $[87] !== groupPrice) {
    t28 = <StrikeThroughPrice price={groupPrice} discount={groupDiscount} formatPrice={formatPrice} />;
    $[85] = formatPrice;
    $[86] = groupDiscount;
    $[87] = groupPrice;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t27 || $[90] !== t28) {
    t29 = <div className="mt-4 mb-6 flex w-full items-center justify-between">{t27}{t28}</div>;
    $[89] = t27;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== isLoading || $[93] !== lineItemGroup.groupId || $[94] !== t25 || $[95] !== t29) {
    t30 = <li key={t6} className="text-sm not-last:border-b" aria-busy={isLoading}>{t25}{t29}</li>;
    $[92] = isLoading;
    $[93] = lineItemGroup.groupId;
    $[94] = t25;
    $[95] = t29;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  return t30;
};